/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * Meeting BaaS API
 * OpenAPI spec version: 1.1
 */
import { z as zod } from "zod"

/**
 * Retrieves all Zoom OAuth connections associated with the authenticated account. Each connection represents a Zoom user who has authorized your app via OAuth. Use this to display connected users or to find the `zoom_user_id` needed for the `zoom_obf_token_user_id` bot parameter. Sensitive token data is never included in the response.
 * @summary List Zoom OAuth Connections
 */
export const listZoomOauthConnectionsResponseItem = zod
  .object({
    connection_failure_data: zod
      .any()
      .optional()
      .describe(
        "Details about why the connection failed. Only present when `state` is `refresh_failed`."
      ),
    created_at: zod.string().describe("Timestamp when the connection was first created."),
    scopes: zod
      .string()
      .nullish()
      .describe("OAuth scopes granted by the user during authorization (e.g. `user:read:user`)."),
    state: zod
      .string()
      .describe(
        "Connection state. `connected` means tokens are valid; `refresh_failed` means the token refresh failed and the user needs to re-authorize."
      ),
    updated_at: zod
      .string()
      .describe("Timestamp when the connection was last updated (e.g. token refresh)."),
    uuid: zod.string().uuid().describe("Unique identifier for this connection."),
    zoom_account_id: zod
      .string()
      .nullish()
      .describe("The Zoom account ID that the connected user belongs to."),
    zoom_user_id: zod
      .string()
      .describe(
        "The Zoom user ID of the connected user. Use this value as `zoom_obf_token_user_id` when creating a bot to have the system automatically fetch an OBF token for this user."
      )
  })
  .describe("A stored Zoom OAuth connection. Tokens are managed server-side and never exposed.")
export const listZoomOauthConnectionsResponse = zod.array(listZoomOauthConnectionsResponseItem)

/**
 * Exchanges a Zoom OAuth authorization code for access and refresh tokens, retrieves the Zoom user's profile, and stores the connection for managed OBF token generation. The authorization code is obtained by directing a Zoom user through the OAuth consent flow for your Zoom OAuth app. Once stored, you can reference this connection's `zoom_user_id` as the `zoom_obf_token_user_id` parameter when creating a bot, and the system will automatically fetch a fresh OBF token at join time. Note: the authorization code is single-use and expires in approximately 10 minutes.
 * @summary Create Zoom OAuth Connection
 */
export const createZoomOauthConnectionBody = zod.object({
  authorization_code: zod
    .string()
    .describe(
      "The OAuth authorization code received from Zoom after user consent. This is a single-use code that expires in approximately 10 minutes."
    ),
  redirect_uri: zod
    .string()
    .describe(
      "The redirect URI that was used in the OAuth authorization request. Must match exactly what was configured in your Zoom OAuth app."
    ),
  zoom_client_id: zod
    .string()
    .describe(
      "Your Zoom OAuth app's Client ID, found in the Zoom App Marketplace under your app's credentials."
    ),
  zoom_client_secret: zod
    .string()
    .describe(
      "Your Zoom OAuth app's Client Secret, found in the Zoom App Marketplace under your app's credentials."
    )
})

/**
 * Retrieves a specific Zoom OAuth connection by its UUID. Returns the connection details including the Zoom user ID, account ID, connection state, and granted scopes. Sensitive token data is never included in the response.
 * @summary Get Zoom OAuth Connection
 */
export const getZoomOauthConnectionParams = zod.object({
  uuid: zod.string().describe("The UUID identifier")
})

export const getZoomOauthConnectionResponse = zod
  .object({
    connection_failure_data: zod
      .any()
      .optional()
      .describe(
        "Details about why the connection failed. Only present when `state` is `refresh_failed`."
      ),
    created_at: zod.string().describe("Timestamp when the connection was first created."),
    scopes: zod
      .string()
      .nullish()
      .describe("OAuth scopes granted by the user during authorization (e.g. `user:read:user`)."),
    state: zod
      .string()
      .describe(
        "Connection state. `connected` means tokens are valid; `refresh_failed` means the token refresh failed and the user needs to re-authorize."
      ),
    updated_at: zod
      .string()
      .describe("Timestamp when the connection was last updated (e.g. token refresh)."),
    uuid: zod.string().uuid().describe("Unique identifier for this connection."),
    zoom_account_id: zod
      .string()
      .nullish()
      .describe("The Zoom account ID that the connected user belongs to."),
    zoom_user_id: zod
      .string()
      .describe(
        "The Zoom user ID of the connected user. Use this value as `zoom_obf_token_user_id` when creating a bot to have the system automatically fetch an OBF token for this user."
      )
  })
  .describe("A stored Zoom OAuth connection. Tokens are managed server-side and never exposed.")

/**
 * Permanently deletes a Zoom OAuth connection by its UUID, removing all stored tokens. After deletion, bots using this connection's `zoom_user_id` as `zoom_obf_token_user_id` will no longer be able to automatically fetch OBF tokens. The Zoom user would need to re-authorize to create a new connection.
 * @summary Delete Zoom OAuth Connection
 */
export const deleteZoomOauthConnectionParams = zod.object({
  uuid: zod.string().describe("The UUID identifier")
})
